import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, CheckSquare, PawPrint, Calendar, ArrowRight, Quote, ShieldCheck, Download, MapPin, Loader2, Check } from 'lucide-react';
import { GuidesMapSection } from './GuidePages';
import { PackOfTheMonth } from '../components/studio/PackFeature';
import { FoundersCollections } from '../components/studio/FoundersCollections';
import { useSiteImage, useSiteText, assetUrl } from '../lib/siteAssets';
import { usePageSeo, DEFAULT_DESCRIPTION } from '../lib/seo';
import { track } from '../lib/analytics';
const FEATURED_DEFAULT = 'https://static.prod-images.emergentagent.com/jobs/6fa42141-fdc2-4ef8-946b-3af9a9eb53ac/images/9817c13b95d84ca6577280d29b473f53535f510d681988edad29bf11da9cc6a8.png';
const ABOUT_DEFAULT = 'https://static.prod-images.emergentagent.com/jobs/6fa42141-fdc2-4ef8-946b-3af9a9eb53ac/images/10b4a3bdbe405c716fe7bf881739dff3090bd407a10d9d8f83085a087f9a098c.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = 'https://images.unsplash.com/photo-1516108317508-6788f6a160e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400';

const DEFAULT_REVIEWS = [
  { id: 'd1', quote: "The Italy kit saved us months of research. Everything was organised beautifully and so easy to follow.", author: "Sarah M.", location: "Sydney → Florence" },
  { id: 'd2', quote: "Finally, a guide that understands the Australian perspective on moving to Europe. Faultless.", author: "James L.", location: "Melbourne → Rome" },
  { id: 'd3', quote: "The pet relocation guide was invaluable. Our cat made it to Italy safely, stress-free.", author: "Emma T.", location: "Brisbane → Milan" },
];

const HomePage = () => {
  usePageSeo('home', { title: '', description: DEFAULT_DESCRIPTION });
  const [seasonal, setSeasonal] = useState({});
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hero/active`).then((r) => setSeasonal(r.data.hero || {})).catch(() => {}); }, []);
  const baseHeroSrc = useSiteImage('hero_image', HERO_IMG);
  const heroSrc = seasonal.image ? assetUrl(seasonal.image) : baseHeroSrc;
  const featuredSrc = useSiteImage('featured_product_image', FEATURED_DEFAULT);
  const aboutSrc = useSiteImage('about_image', ABOUT_DEFAULT);
  const heroEyebrowBase = useSiteText('hero_eyebrow', 'The Australian Expat Concierge');
  const heroHeadlineBase = useSiteText('hero_headline', 'Your move to Italy,');
  const heroAccentBase = useSiteText('hero_headline_accent', 'beautifully planned.');
  const heroSubBase = useSiteText('hero_subheadline', 'Considered, comprehensive relocation kits for Australians crossing to Europe — from visas and finances to finding your new home and settling your pets.');
  const ctaPrimaryBase = useSiteText('hero_cta_primary', 'Browse the Collection');
  const ctaSecondaryBase = useSiteText('hero_cta_secondary', 'Get the Free Checklist');
  const heroEyebrow = seasonal.eyebrow || heroEyebrowBase;
  const heroHeadline = seasonal.headline || heroHeadlineBase;
  const heroAccent = seasonal.headline && !seasonal.accent ? '' : (seasonal.accent || heroAccentBase);
  const heroSub = seasonal.subheadline || heroSubBase;
  const ctaPrimary = seasonal.cta_primary || ctaPrimaryBase;
  const ctaSecondary = seasonal.cta_secondary || ctaSecondaryBase;
  const [testimonials, setTestimonials] = useState(DEFAULT_REVIEWS);
  const [waitlist, setWaitlist] = useState({ country: null, email: '', loading: false });
  const [joined, setJoined] = useState({});

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then((r) => { if (r.data.reviews && r.data.reviews.length) setTestimonials(r.data.reviews); })
      .catch(() => {});
  }, []);

  const submitWaitlist = async (country) => {
    if (!waitlist.email || !/\S+@\S+\.\S+/.test(waitlist.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setWaitlist((w) => ({ ...w, loading: true }));
    try {
      const res = await axios.post(`${API}/waitlist`, { email: waitlist.email, country });
      track('sign_up', { method: 'waitlist', country });
      toast.success(res.data.message || `You're on the ${country} waitlist!`);
      setJoined((j) => ({ ...j, [country]: true }));
      setWaitlist({ country: null, email: '', loading: false });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
      setWaitlist((w) => ({ ...w, loading: false }));
    }
  };

  const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  };

  const kitFeatures = [
    { icon: FileText, title: 'Visa & Residency', description: 'Step-by-step guidance through every form, appointment and approval.' },
    { icon: CheckSquare, title: 'Departure Checklists', description: 'A considered countdown so nothing is left to the last fraught week.' },
    { icon: PawPrint, title: 'Pet Relocation', description: 'Bring your whole family — paperwork, flights and quarantine, handled.' },
    { icon: Calendar, title: 'First 90 Days', description: 'A gentle roadmap for settling into la dolce vita with confidence.' },
  ];

  const comingSoon = ['Portugal', 'Spain', 'Greece', 'France'];

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* ======================= HERO (light) ======================= */}
      <section data-testid="hero-section" data-seasonal={seasonal.name ? "true" : "false"} className="relative min-h-screen flex items-end overflow-hidden grain-overlay">
        <div className="absolute inset-0">
          <img src={heroSrc} alt="Golden hour over a Tuscan villa" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#FAF7F2]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FAF7F2] via-[#FAF7F2]/85 to-[#FAF7F2]/35" />
        </div>

        <div className="container-custom relative z-10 pb-24 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <span className="eyebrow mb-6" data-testid="hero-eyebrow">
              <span className="w-10 h-px bg-[#C5A059]" /> {heroEyebrow}
            </span>
            <h1 data-testid="hero-headline" className="font-display text-[#1E1622] font-medium leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mb-7">
              {heroHeadline}
              {heroAccent && <span className="block italic text-[#3B164C]">{heroAccent}</span>}
            </h1>
            <p data-testid="hero-subheadline" className="text-lg text-[#1E1622]/70 leading-relaxed mb-10 max-w-xl font-light">
              {heroSub}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/shop" data-testid="hero-cta-shop" className="btn-gold inline-flex items-center justify-center gap-2">
                {ctaPrimary} <ArrowRight size={18} />
              </Link>
              <Link to="/freebie" data-testid="hero-cta-freebie"
                className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 font-medium text-[#3B164C] border border-[#3B164C]/40 hover:border-[#C5A059] hover:text-[#C5A059] transition-all duration-300">
                {ctaSecondary}
              </Link>
            </div>

            <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-[#1E1622]/60">
              <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-[#C5A059]" /> Secure checkout</span>
              <span className="hidden sm:block w-px h-4 bg-[#1E1622]/20" />
              <span className="flex items-center gap-2"><Download size={15} className="text-[#C5A059]" /> Instant delivery</span>
              <span className="hidden sm:block w-px h-4 bg-[#1E1622]/20" />
              <span className="flex items-center gap-2"><MapPin size={15} className="text-[#C5A059]" /> Australian-made</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ======================= FEATURES ======================= */}
      <section data-testid="features-section" className="section-padding bg-[#FAF7F2]">
        <div className="container-custom">
          <motion.div {...fadeUp} className="max-w-2xl mb-16">
            <span className="eyebrow mb-5"><span className="w-10 h-px bg-[#C5A059]" /> 01 — What's inside</span>
            <h2 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622] leading-tight">
              Every detail of the move, considered in advance.
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-[#C5A059]/25">
            {kitFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group py-10 lg:px-8 border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#C5A059]/25"
              >
                <div className="w-14 h-14 mb-7 rounded-full border border-[#C5A059]/40 flex items-center justify-center text-[#C5A059] transition-all duration-300 group-hover:bg-[#3B164C] group-hover:border-[#3B164C] group-hover:text-[#E5C178]">
                  <feature.icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl font-medium text-[#1E1622] mb-3">{feature.title}</h3>
                <p className="text-sm text-[#1E1622]/60 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================= FEATURED PRODUCT ======================= */}
      <section data-testid="featured-product-section" className="section-padding bg-[#F5EFE9]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <motion.div {...fadeUp} className="relative">
              <div className="absolute -inset-3 border border-[#C5A059]/40 rounded-[2rem] hidden md:block" />
              <img
                src={featuredSrc}
                alt="The Complete Australia to Italy Relocation Kit"
                className="relative w-full h-auto rounded-[1.75rem] shadow-[0_30px_60px_rgba(45,16,55,0.18)]"
              />
            </motion.div>
            <motion.div {...fadeUp}>
              <span className="eyebrow mb-5"><span className="w-10 h-px bg-[#C5A059]" /> 02 — Currently available</span>
              <h2 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622] leading-tight mb-6">
                The Complete Australia&nbsp;to&nbsp;Italy Relocation Kit
              </h2>
              <p className="text-[#1E1622]/70 leading-relaxed mb-8 font-light text-lg">
                Everything you need for your move, in one considered package — visas, checklists,
                pet relocation, banking, healthcare, and your first ninety days.
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-10">
                {['Visa & residency guides', 'Pre-departure checklists', 'Pet relocation guide', 'Banking & healthcare setup', 'First 90 days planner', 'Cost-of-living calculator'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#1E1622]/80 text-sm">
                    <span className="w-6 h-6 rounded-full border border-[#C5A059]/50 flex items-center justify-center flex-shrink-0">
                      <CheckSquare size={12} className="text-[#C5A059]" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-6">
                <span className="font-display text-4xl font-medium text-[#3B164C]">$49 <span className="text-lg align-top text-[#C5A059]">AUD</span></span>
                <Link to="/shop" data-testid="featured-product-cta" className="btn-primary inline-flex items-center gap-2">
                  Shop Now <ArrowRight size={18} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <FoundersCollections variant="home" />
      <PackOfTheMonth />
      <GuidesMapSection />

      {/* ======================= ABOUT ======================= */}
      <section data-testid="about-section" className="section-padding bg-[#FAF7F2]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <motion.div {...fadeUp} className="relative lg:order-2">
              <div className="absolute -inset-3 border border-[#C5A059]/40 rounded-[2rem] hidden md:block" />
              <img
                src={aboutSrc}
                alt="Founder of The Expat Studio"
                className="relative w-full max-w-md mx-auto rounded-[1.75rem] shadow-[0_30px_60px_rgba(45,16,55,0.18)]"
              />
            </motion.div>
            <motion.div {...fadeUp} className="lg:order-1">
              <span className="eyebrow mb-5"><span className="w-10 h-px bg-[#C5A059]" /> Our story</span>
              <h2 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622] leading-tight mb-6">
                Built by an expat, for expats.
              </h2>
              <p className="text-[#1E1622]/70 leading-relaxed mb-5 font-light text-lg">
                Founded by an Australian navigating her own relocation to Italy, The Expat Studio
                creates the guides she wished existed.
              </p>
              <p className="text-[#1E1622]/70 leading-relaxed mb-8 font-light">
                Every checklist is researched, road-tested and designed to turn overwhelming
                paperwork into a clear, beautiful plan.
              </p>
              <Link to="/about" data-testid="about-cta" className="link-gold group">
                Read Our Story
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ======================= TESTIMONIALS (live reviews) ======================= */}
      <section data-testid="testimonials-section" className="section-padding bg-[#2D1037] relative overflow-hidden grain-overlay">
        <div className="container-custom relative z-10">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
            <span className="eyebrow justify-center mb-5 text-[#E5C178]"><span className="w-10 h-px bg-[#C5A059]" /> Kind words</span>
            <h2 className="font-display text-4xl md:text-5xl font-medium text-[#FAF7F2]">
              Loved by Australians making the leap.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, index) => (
              <motion.div
                key={t.id || index}
                data-testid={`testimonial-${index}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.12 }}
                className="rounded-2xl p-8 border border-[#C5A059]/20 bg-[#FAF7F2]/[0.04] backdrop-blur-sm"
              >
                <Quote size={32} className="text-[#C5A059] mb-5" />
                <p className="font-display text-xl italic text-[#FAF7F2]/90 leading-relaxed mb-8">
                  {t.quote}
                </p>
                <div className="pt-5 border-t border-[#FAF7F2]/10">
                  <p className="font-semibold text-[#FAF7F2]">{t.author}</p>
                  <p className="text-sm text-[#E5C178] tracking-wide">{t.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ======================= COMING SOON (waitlist capture) ======================= */}
      <section data-testid="coming-soon-section" className="section-padding bg-[#F5EFE9]">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
            <span className="eyebrow justify-center mb-5"><span className="w-10 h-px bg-[#C5A059]" /> On the horizon</span>
            <h2 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622] mb-4">
              More destinations, arriving soon.
            </h2>
            <p className="text-[#1E1622]/60 font-light">
              Join the waitlist to be the first to know when new relocation kits are released.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
            {comingSoon.map((country, index) => (
              <motion.div
                key={country}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white rounded-2xl border border-[#C5A059]/20 p-10 text-center transition-all duration-300 hover:border-[#C5A059]/50"
              >
                <span className="font-display text-3xl text-[#C5A059] mb-5 inline-flex w-16 h-16 items-center justify-center rounded-full border border-[#C5A059]/30">
                  {country[0]}
                </span>
                <h3 className="font-display text-2xl font-medium text-[#1E1622] mb-4">{country}</h3>

                {joined[country] ? (
                  <p data-testid={`waitlist-joined-${country.toLowerCase()}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#3B164C] font-semibold">
                    <Check size={14} className="text-[#C5A059]" /> You're on the list
                  </p>
                ) : waitlist.country === country ? (
                  <div className="space-y-3">
                    <input
                      type="email"
                      autoFocus
                      value={waitlist.email}
                      onChange={(e) => setWaitlist((w) => ({ ...w, email: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitWaitlist(country); }}
                      placeholder="you@email.com"
                      data-testid={`waitlist-input-${country.toLowerCase()}`}
                      className="w-full text-sm px-4 py-2.5 rounded-full border border-[#C5A059]/40 focus:border-[#C5A059] focus:outline-none text-center"
                    />
                    <button
                      onClick={() => submitWaitlist(country)}
                      disabled={waitlist.loading}
                      data-testid={`waitlist-submit-${country.toLowerCase()}`}
                      className="w-full btn-gold text-sm py-2.5 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {waitlist.loading ? <Loader2 size={14} className="animate-spin" /> : 'Notify Me'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setWaitlist({ country, email: '', loading: false })}
                    data-testid={`waitlist-${country.toLowerCase()}`}
                    className="text-xs uppercase tracking-[0.2em] text-[#3B164C] font-semibold hover:text-[#C5A059] transition-colors"
                  >
                    Join Waitlist
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================= CTA ======================= */}
      <section data-testid="cta-section" className="relative py-24 md:py-32 bg-[#3B164C] overflow-hidden grain-overlay">
        <div className="container-custom relative z-10 text-center">
          <motion.div {...fadeUp} className="max-w-2xl mx-auto">
            <span className="eyebrow justify-center mb-6 text-[#E5C178]"><span className="w-10 h-px bg-[#C5A059]" /> Begin</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-[#FAF7F2] mb-6 leading-tight">
              Ready to start your relocation journey?
            </h2>
            <p className="text-[#FAF7F2]/75 mb-10 font-light text-lg">
              Download our free guide and discover what nobody tells you about moving overseas.
            </p>
            <Link to="/freebie" data-testid="final-cta" className="btn-gold inline-flex items-center gap-2">
              Get Your Free Guide <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
